import { ChangeEvent, useRef, useState } from 'react'
import { useAppDispatch, useAppSelector } from '@/hooks'
import { currentUser } from '@/reducers'
import {
  UPLOAD_UTILITY_BILL_SUCCESS,
  uploadUtilityBillForm,
} from '@/actions/plans'
import { RSAAResultAction } from 'redux-api-middleware'
import { User } from '@/types'
import { AccountFieldKey, AccountFieldsConfig } from '@/types/utilityProgram'
import {
  buildUploadSuccessMessage,
  isValidExtractedValue,
} from './utilityAccountFields.helpers'
import { BillUploadStatus } from './BillUploadCard'

export type BillExtraction = Partial<Record<AccountFieldKey, string>>

const MAX_FILE_SIZE_MB = 10

type StoredBillUpload = {
  extraction?: BillExtraction
  s3_url?: string | null
}

function readStoredBillUpload(
  user: User | null | undefined,
): StoredBillUpload | undefined {
  return (
    (user?.profile?.utility_bill_upload as
      | StoredBillUpload
      | null
      | undefined) ?? undefined
  )
}

/** Reads the AI extraction object the backend stores on the user profile. */
export function readBillExtraction(
  user: User | null | undefined,
): BillExtraction | undefined {
  return readStoredBillUpload(user)?.extraction
}

type UseUtilityBillUploadOptions = {
  /** Drives which extracted fields are kept (only configured fields autofill). */
  config: AccountFieldsConfig
  /** Forwarded to the extraction endpoint when present (enrollment flow). */
  utilityProgramId?: number | string
}

/**
 * Owns the "upload a utility bill" interaction and its AI extraction: file
 * validation, the PUT request, reading the extraction off the user profile, and
 * the success/error messaging. Shared by the enrollment account step and the
 * profile utility-account page.
 *
 * The hook intentionally does not touch any form state — consumers react to
 * `extractedValues` to populate their own fields.
 */
export function useUtilityBillUpload({
  config,
  utilityProgramId,
}: UseUtilityBillUploadOptions) {
  const dispatch = useAppDispatch()
  const user = useAppSelector(currentUser)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [fileUploadStatus, setFileUploadStatus] =
    useState<BillUploadStatus>('idle')
  const [fileUploadName, setFileUploadName] = useState<string | null>(null)
  const [uploadErrorMessage, setUploadErrorMessage] = useState<{
    title: string
    subtitle: string
  } | null>(null)
  const [uploadSuccessMessage, setUploadSuccessMessage] = useState<
    string | null
  >(null)
  const [billUrl, setBillUrl] = useState<string | null>(null)
  const [extractedValues, setExtractedValues] = useState<BillExtraction>({})

  const pickConfiguredExtraction = (
    extraction: BillExtraction | undefined,
  ): BillExtraction => {
    const picked: BillExtraction = {}
    for (const field of config) {
      const value = extraction?.[field.field]
      if (isValidExtractedValue(value)) {
        picked[field.field] = value
      }
    }
    return picked
  }

  /**
   * Surfaces an extraction (from this page's upload, or one carried over from an
   * earlier step) — sets the success banner and the per-field extracted values.
   * Returns how many configured fields were filled.
   */
  const applyExtraction = (
    extraction: BillExtraction | undefined,
    fileName?: string,
    url?: string,
  ): number => {
    const picked = pickConfiguredExtraction(extraction)
    const count = Object.keys(picked).length
    if (count > 0) {
      setExtractedValues(picked)
      setUploadSuccessMessage(buildUploadSuccessMessage(count))
      setUploadErrorMessage(null)
      setFileUploadStatus('success')
      if (fileName) {
        setFileUploadName(fileName)
      }
      if (url) {
        setBillUrl(url)
      }
    }
    return count
  }

  const uploadUtilityBill = async (file: File) => {
    setFileUploadStatus('loading')
    setUploadErrorMessage(null)
    setUploadSuccessMessage(null)
    setFileUploadName(null)
    setBillUrl(null)
    setExtractedValues({})

    if (file.size / 1024 / 1024 > MAX_FILE_SIZE_MB) {
      setFileUploadStatus('error')
      setFileUploadName(file.name)
      setUploadErrorMessage({
        title: `${file.name} has an error.`,
        subtitle: 'File size is too large, upload a file smaller than 10MB.',
      })
      return
    }

    const data = new FormData()
    data.append('file', file)

    const uploadResponse: RSAAResultAction = await dispatch(
      uploadUtilityBillForm(data, true, false, utilityProgramId),
    )

    if (uploadResponse.type === UPLOAD_UTILITY_BILL_SUCCESS) {
      setFileUploadName(file.name)
      const updatedUser = (uploadResponse as { payload?: User }).payload ?? user
      const count = applyExtraction(
        readBillExtraction(updatedUser),
        file.name,
        readStoredBillUpload(updatedUser)?.s3_url ?? undefined,
      )
      if (count === 0) {
        // Upload succeeded but nothing usable was extracted.
        setFileUploadStatus('error')
        setUploadErrorMessage({
          title: `${file.name} has an error.`,
          subtitle:
            "We couldn't find your account details, please make sure they're visible on the bill. See example below.",
        })
      }
    } else {
      setFileUploadStatus('error')
      setFileUploadName(file.name)
      setUploadErrorMessage({
        title: 'An unexpected error occurred.',
        subtitle:
          'Please try again or enter your account details manually below.',
      })
    }
  }

  const handleFilesUploaded = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    // Clear the input so selecting the same file again still fires onChange.
    event.target.value = ''
    if (!file) {
      return
    }
    await uploadUtilityBill(file)
  }

  const handleUploadClick = () => {
    fileInputRef.current?.click()
  }

  return {
    fileInputRef,
    handleFilesUploaded,
    handleUploadClick,
    fileUploadStatus,
    fileUploadName,
    uploadErrorMessage,
    uploadSuccessMessage,
    /** URL of the uploaded bill, used to link the file name in the banner. */
    billUrl,
    /** Values extracted from a bill uploaded on this page, for autofilling fields. */
    extractedValues,
    /**
     * Valid extracted values already stored on the user profile. For display as
     * the "Extracted from bill" label only — never used to populate the fields,
     * which are driven by the saved utility account.
     */
    persistedExtraction: pickConfiguredExtraction(
      readStoredBillUpload(user)?.extraction,
    ),
  }
}
